import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from "lucide-react";
import type * as React from "react";
import type { TableSort } from "@/core/hooks/use-table-sort";
import { cn } from "@/core/lib/utils";
import { TableHead } from "../ui/table";

/**
 * A column header that sorts its table. Shows ⇅ while inactive and ↑ / ↓ for
 * the active direction; the whole label is the button. Pair with
 * `useTableSort`, passing its `sort` and `toggle`.
 */
export function SortableTableHead<K extends string>({
  column,
  sort,
  onSort,
  align = "left",
  className,
  children,
}: {
  column: K;
  sort: TableSort<K>;
  onSort: (column: K) => void;
  align?: "left" | "center" | "right";
  className?: string;
  children: React.ReactNode;
}) {
  const active = sort?.key === column;
  const Icon = !active ? ChevronsUpDownIcon : sort.dir === "asc" ? ArrowUpIcon : ArrowDownIcon;

  return (
    <TableHead
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        align === "center" && "text-center",
        align === "right" && "text-right",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "-mx-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 uppercase tracking-wide transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
          align === "right" && "flex-row-reverse",
          active && "text-foreground",
        )}
      >
        {children}
        <Icon
          aria-hidden
          className={cn("size-3.5 shrink-0", active ? "text-foreground" : "opacity-50")}
        />
      </button>
    </TableHead>
  );
}
